import { buildEmployeeListWhereFilter } from '@/employees/application/adapters/listEmployees';
import { useListCrewMembers } from '@/jobs/application/adapters/getCrewMembers';
import { useMemo } from 'react';
import { CrewField } from '../RaptorJobCreateModal';
import { useGetEmployeeFormOptions } from '@/employees/application/adapters/getEmployeeFormOptions';
import { useEmployeeSearch } from '@/employees/presentation/hooks';

const SUPPORTED_RAPTOR_JOB_ROLES = [1, 4, 8, 9, 10];
const EMPLOYEE_QUERY_LIMIT = 100;

interface UseCrewSelectorProps {
  crewFields: CrewField[];
  setCrewFields: (fields: CrewField[]) => void;
  recurrence: 'single' | 'multi';
  startDate?: Date | null;
  endDate?: Date | null;
}

export const useCrewSelector = ({ crewFields, setCrewFields, recurrence, startDate, endDate }: UseCrewSelectorProps) => {
  const { availableJobRoles: jobRoles, loading: loadingJobRoles } = useGetEmployeeFormOptions({ skip: false });
  const { debouncedSearchTerm: employeeDebouncedSearchTerm, setSearchTerm: setEmployeeSearchTerm } = useEmployeeSearch();

  const supportedJobRoles = useMemo(() => jobRoles?.filter((role) => SUPPORTED_RAPTOR_JOB_ROLES.includes(role.id)) ?? [], [jobRoles]);

  const employeeWhereCause = useMemo(() => buildEmployeeListWhereFilter(employeeDebouncedSearchTerm), [employeeDebouncedSearchTerm]);

  const referenceEndDate = recurrence === 'multi' ? (endDate ?? undefined) : (startDate ?? undefined);

  const { crewMembers, loading: loadingEmployees } = useListCrewMembers(
    {
      where: employeeWhereCause,
      skip: 0,
      take: EMPLOYEE_QUERY_LIMIT
    },
    startDate ?? undefined,
    referenceEndDate
  );

  const buildEmployeeOptionsForRole = useMemo(() => {
    return (selectedRoleId: number | null) => {
      if (!crewMembers || selectedRoleId == null) return [];

      // Remove duplicates and filter by role
      const uniqueCrewMembers = Array.from(new Map(crewMembers.map((e) => [e.id, e])).values());
      const filteredByRole = uniqueCrewMembers.filter((employee) =>
        employee.jobRoles?.some((role) => supportedJobRoles.some((supportedRole) => supportedRole.id === role.id))
      );

      return filteredByRole.map((employee) => ({
        id: employee.id,
        name: employee.name || `Employee ${employee.id}`,
        label: employee.name || `Employee ${employee.id}`,
        value: employee.id,
        status: employee.status || []
      }));
    };
  }, [crewMembers, supportedJobRoles]);

  const handleDeleteCrewField = (id: string) => {
    setCrewFields(crewFields.filter((field) => field.rowId !== id));
  };

  const handleRoleChange = (rowId: string, roleId: number, roleName: string) => {
    const updatedCrewFields = crewFields.map((crew) =>
      crew.rowId === rowId ? { ...crew, roleId, roleName, employeeId: null, employeeName: '' } : crew
    );
    setCrewFields(updatedCrewFields);
    setEmployeeSearchTerm('');
  };

  const handleEmployeeSelect = (rowId: string, employeeId: number | null, employeeName: string, status?: string[]) => {
    if (!employeeId) {
      const updatedCrewFields = crewFields.map((crew) =>
        crew.rowId === rowId ? { ...crew, employeeId: null, employeeName: '', status: undefined } : crew
      );

      setCrewFields(updatedCrewFields);
      return;
    }

    const updatedCrewFields = crewFields.map((crew) => (crew.rowId === rowId ? { ...crew, employeeId, employeeName, status } : crew));
    setCrewFields(updatedCrewFields);
  };

  return {
    jobRoles,
    loadingJobRoles,
    employeeDebouncedSearchTerm,
    setEmployeeSearchTerm,
    crewMembers,
    loadingEmployees,
    buildEmployeeOptionsForRole,
    handleDeleteCrewField,
    handleRoleChange,
    handleEmployeeSelect,
    supportedJobRoles
  };
};
